import React, { useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { T, UI, FONT } from "../constants/tokens";
import { TYPE } from "../constants/typography";
import { useIsMobile } from "../utils/hooks";
import SymbolAutocomplete from "./SymbolAutocomplete";
import Icon from "./Icon";
import DateField from "./DateField";

// ─── CalculatorsTab ──────────────────────────────────────────────────────────
const fmt2 = (n) => n.toLocaleString("he-IL", { maximumFractionDigits: 2 });

function CalculatorsTab() {
  const isMobile = useIsMobile();
  const [calc, setCalc] = useState("tax");

  // ── Tax calculator state ──────────────────────────
  const [buyPrice,      setBuyPrice]      = useState("165.20");
  const [sellPrice,     setSellPrice]     = useState("220.00");
  const [sharesNum,     setSharesNum]     = useState("100");
  const [buyDate,       setBuyDate]       = useState("2022-01-01");
  const [taxType,       setTaxType]       = useState("IL");   // IL=25% US=15%

  // ── Dividend calculator state ─────────────────────
  const [divSymbol,     setDivSymbol]     = useState("AAPL");
  const [divShares,     setDivShares]     = useState("200");
  const [annualDivPS,   setAnnualDivPS]   = useState("1.00");
  const [divYieldPct,   setDivYieldPct]   = useState("0.53");
  const [divGrowthPct,  setDivGrowthPct]  = useState("5");
  const [divYears,      setDivYears]      = useState(10);
  const [divTaxType,    setDivTaxType]    = useState("IL");

  // ── Tax calculations ──────────────────────────────
  const bp   = parseFloat(buyPrice)  || 0;
  const sp   = parseFloat(sellPrice) || 0;
  const sh   = parseFloat(sharesNum) || 0;
  const rate = taxType === "IL" ? 0.25 : 0.15;
  const gross      = (sp - bp) * sh;
  const taxAmount  = gross > 0 ? gross * rate : 0;
  const netProfit  = gross - taxAmount;
  const holdYears  = buyDate ? Math.max(0, (new Date() - new Date(buyDate)) / (365.25 * 24 * 3600 * 1000)) : 0;

  const taxProjection = Array.from({ length: 21 }, (_, i) => ({
    year: i,
    value: Math.round(netProfit > 0 ? netProfit * Math.pow(1.08, i) : 0),
  }));

  // ── Dividend calculations ─────────────────────────
  const dShares   = parseFloat(divShares)    || 0;
  const dDivPS    = parseFloat(annualDivPS)  || 0;
  const dYield    = parseFloat(divYieldPct)  || 0;
  const dGrowth   = parseFloat(divGrowthPct) / 100 || 0;
  const dTaxRate  = divTaxType === "IL" ? 0.25 : 0.15;
  const stockPrice = dYield > 0 ? (dDivPS / (dYield / 100)) : 0;

  const annualDivNow = dShares * dDivPS;
  const monthlyDiv   = annualDivNow / 12;
  const taxOnDiv     = annualDivNow * dTaxRate;
  const netAnnual    = annualDivNow - taxOnDiv;

  const dripData = (() => {
    let s = dShares, cumDiv = 0;
    return Array.from({ length: 21 }, (_, i) => {
      const yr      = i;
      const dps     = dDivPS * Math.pow(1 + dGrowth, yr);
      const gross   = s * dps;
      const netD    = gross * (1 - dTaxRate);
      cumDiv += netD;
      const newSh   = stockPrice > 0 ? netD / stockPrice : 0;
      s += newSh;
      return { year: yr, shares: +s.toFixed(2), annualDiv: +gross.toFixed(2), netDiv: +netD.toFixed(2), cumDiv: +cumDiv.toFixed(2), dps: +dps.toFixed(3) };
    });
  })();


  // ── shared input style ────────────────────────────
  const INP = { width:"100%", border:`1.5px solid ${UI.surface.sunken}`, borderRadius:6, padding:"10px 12px", fontSize:14, fontFamily:FONT.body, outline:"none", color:UI.text.primary, background:UI.surface.page, boxSizing:"border-box" };
  const LBL = { fontSize:12, fontWeight:700, color:UI.text.secondary, marginBottom:4, display:"block" };
  const ROW = { display:"flex", gap:10, flexWrap:"wrap" };
  const HALF = { flex:"1 1 140px", minWidth:0 };
  // A calculator result is always positive and has no red counterpart, so
  // green would only be saying "good". Emphasis carries it instead.
  const ResultCard = ({ label, value, sub, green }) => (
    <div style={{ background:green ? UI.fill.subtle : UI.surface.raised, border:`1.5px solid ${green ? UI.line.strong : UI.line.hairline}`, borderRadius:6, padding:"12px 14px" }}>
      <div style={{ fontSize:11, color:UI.text.secondary, fontWeight:700, marginBottom:4 }}>{label}</div>
      <div style={{ fontSize:18, fontWeight:800, color:UI.text.primary }}>{value}</div>
      {sub && <div style={{ fontSize:10, color:UI.text.muted, marginTop:2 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12, paddingBottom:20 }}>

      {/* Header */}
      <div style={{ background:UI.surface.raised, borderRadius:T.radius.xl, padding:"18px 22px", color:UI.text.primary, boxShadow:UI.shadow.card, border:`1px solid ${UI.line.hairline}`, borderTop:`2px solid ${UI.line.strong}`, display:"flex", alignItems:"center", gap:16 }}>
        {/* Modern calculator SVG icon */}
        <div style={{ width:isMobile?46:54, height:isMobile?46:54, borderRadius:6, background:UI.fill.subtle, border:`1px solid ${UI.line.strong}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <svg width={isMobile?26:30} height={isMobile?26:30} viewBox="0 0 24 24" fill="none">
            <rect x="4" y="3" width="16" height="18" rx="2.5" fill={UI.surface.page}/>
            <rect x="7" y="6" width="10" height="3" rx="1" fill={UI.text.secondary}/>
            <circle cx="8" cy="13" r="1.2" fill={UI.text.secondary}/>
            <circle cx="12" cy="13" r="1.2" fill={UI.text.secondary}/>
            <circle cx="16" cy="13" r="1.2" fill={UI.text.secondary}/>
            <circle cx="8" cy="17" r="1.2" fill={UI.text.secondary}/>
            <circle cx="12" cy="17" r="1.2" fill={UI.text.secondary}/>
            <rect x="14.5" y="15.5" width="3" height="3" rx="0.5" fill={UI.text.secondary}/>
          </svg>
        </div>
        <div>
          <div style={TYPE.h1}>מחשבונים פיננסיים</div>
          <div style={{ fontSize:12, color:UI.text.secondary, marginTop:4 }}>חשב מס, דיבידנד ותחזיות השקעה</div>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display:"flex", background:UI.surface.raised, borderRadius:T.radius.xl, boxShadow: UI.shadow.card, padding:4, border:`1px solid ${UI.line.hairline}` }}>
        {[
          { id:"tax",      label:"מס רווחי הון", icon:"bank" },
          { id:"dividend", label:"דיבידנד",       icon:"coins" },
        ].map(c => (
          <button key={c.id} onClick={() => setCalc(c.id)}
            style={{ flex:1, display:"inline-flex", alignItems:"center", justifyContent:"center", gap:6, background:calc===c.id ? UI.state.surface : "transparent", border:"none", borderRadius:6, padding:"10px 4px", fontSize:13, fontWeight:700, color:calc===c.id ? UI.state.fg : UI.text.secondary, cursor:"pointer", transition:"all .15s" }}>
            <Icon name={c.icon} size={15} color={calc===c.id ? UI.state.fg : UI.text.secondary}/> {c.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════
          CALCULATOR 1 — Capital Gains Tax
      ══════════════════════════════════════════════ */}
      {calc === "tax" && (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

          {/* Inputs */}
          <div style={{ background:UI.surface.raised, borderRadius:T.radius.xl, boxShadow: UI.shadow.card, padding:"16px 16px", border:`1px solid ${UI.line.hairline}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, fontWeight:800, fontSize:14, color:UI.text.primary, marginBottom:14 }}><Icon name="download" size={15} color={UI.text.primary}/> נתוני העסקה</div>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div style={ROW}>
                <div style={HALF}>
                  <label style={LBL}>מחיר קנייה ($)</label>
                  <input style={INP} type="number" value={buyPrice} onChange={e => setBuyPrice(e.target.value)} placeholder="165.20"/>
                </div>
                <div style={HALF}>
                  <label style={LBL}>מחיר מכירה ($)</label>
                  <input style={INP} type="number" value={sellPrice} onChange={e => setSellPrice(e.target.value)} placeholder="220.00"/>
                </div>
              </div>
              <div style={ROW}>
                <div style={HALF}>
                  <label style={LBL}>כמות מניות</label>
                  <input style={INP} type="number" value={sharesNum} onChange={e => setSharesNum(e.target.value)} placeholder="100"/>
                </div>
                <div style={HALF}>
                  <label style={LBL}>תאריך קנייה</label>
                  <DateField value={buyDate} onChange={setBuyDate} aria-label="תאריך קנייה" style={{ fontSize: 14, border: `1.5px solid ${UI.surface.sunken}` }}/>
                </div>
              </div>
              {/* Tax type */}
              <div>
                <label style={LBL}>סוג משקיע</label>
                <div style={{ display:"flex", gap:8 }}>
                  {[{id:"IL",label:"ישראלי · 25%"},{id:"US",label:"זר · 15%"}].map(t => (
                    <button key={t.id} onClick={() => setTaxType(t.id)}
                      style={{ flex:1, background:taxType===t.id ? UI.state.surface : UI.surface.page, border:`1.5px solid ${taxType===t.id ? UI.state.surface : UI.line.hairline}`, borderRadius:6, padding:"10px 8px", fontSize:13, fontWeight:700, color:taxType===t.id ? UI.surface.page : UI.text.secondary, cursor:"pointer" }}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          {sh > 0 && bp > 0 && sp > 0 && (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(140px,1fr))", gap:10 }}>
                <ResultCard label="רווח גולמי"          value={`$${fmt2(gross)}`}     sub={gross >= 0 ? "לפני מס" : "הפסד · פטור ממס"}  green={false}/>
                <ResultCard label={`מס (${rate*100}%)`}  value={`$${fmt2(taxAmount)}`} sub={`${holdYears.toFixed(1)} שנות החזקה`}         green={false}/>
                <ResultCard label="רווח נקי אחרי מס"    value={`$${fmt2(netProfit)}`} sub="לאחר תשלום מס"  green={netProfit > 0}/>
              </div>
              {holdYears > 0 && (
                <div style={{ display:"flex", alignItems:"flex-start", gap:6, background:UI.fill.subtle, border:`1.5px solid ${UI.line.strong}`, borderRadius:6, padding:"10px 14px", fontSize:12, color:UI.text.secondary }}>
                  <Icon name="bulb" size={14} color={UI.text.secondary} style={{ marginTop:1, flexShrink:0 }}/>
                  <span>החזקת את המניה {holdYears.toFixed(1)} שנים. בישראל, המס זהה ללא קשר לתקופה (25% על כל רווח הון).</span>
                </div>
              )}
            </div>
          )}

          {/* 20-year projection */}
          {netProfit > 0 && (
            <div style={{ background:UI.surface.raised, borderRadius:T.radius.xl, boxShadow: UI.shadow.card, padding:"16px", border:`1px solid ${UI.line.hairline}` }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, fontWeight:800, fontSize:14, color:UI.text.primary, marginBottom:4 }}><Icon name="trendUp" size={15} color={UI.text.primary}/> תחזית 20 שנה · השקעה מחדש ב-8% שנתי</div>
              <div style={{ fontSize:11, color:UI.text.secondary, marginBottom:14 }}>מה קורה אם הרווח הנקי שלך (${fmt2(netProfit)}) מושקע ונצבר ב-8% תשואה שנתית</div>

              {/* Chart */}
              <div style={{ height:180, marginBottom:16 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={taxProjection} margin={{ top:5, right:8, left:8, bottom:0 }}>
                    <defs>
                      <linearGradient id="taxGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor={UI.categorical[0]} stopOpacity={0.06}/>
                        <stop offset="100%" stopColor={UI.categorical[0]} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={UI.chartGrid} vertical={false}/>
                    <XAxis dataKey="year" tick={{ fontSize:11, fill:UI.text.muted }} axisLine={false} tickLine={false} tickFormatter={v => `${v}y`}/>
                    <YAxis tick={{ fontSize:11, fill:UI.text.muted }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} width={45}/>
                    <Tooltip formatter={v => [`$${fmt2(v)}`, "שווי"]} labelFormatter={l => `שנה ${l}`} contentStyle={{ fontSize:12, borderRadius:6, background:UI.surface.overlay, border:`1px solid ${UI.line.hairline}`, color:UI.text.primary }}/>
                    <Area type="monotone" dataKey="value" stroke={UI.categorical[0]} strokeWidth={1.5} fill="url(#taxGrad)"/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Table */}
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                  <thead>
                    <tr style={{ background:UI.surface.page }}>
                      {["שנה","שווי","רווח מצטבר"].map(h => (
                        <th key={h} style={{ padding:"8px 10px", textAlign:"right", fontWeight:700, color:UI.text.secondary, borderBottom:`2px solid ${UI.surface.sunken}`, whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {taxProjection.filter((_,i) => i % 2 === 0 || i === 20).map(row => (
                      <tr key={row.year} style={{ background: row.year % 4 === 0 ? UI.surface.page : UI.surface.raised, fontWeight: row.year === 20 ? 800 : 400 }}>
                        <td style={{ padding:"6px 10px", color:UI.text.secondary, borderBottom:`1px solid ${UI.surface.sunken}` }}>{row.year === 0 ? "היום" : `שנה ${row.year}`}</td>
                        <td style={{ padding:"6px 10px", fontWeight:700, color:UI.text.primary, borderBottom:`1px solid ${UI.line.hairline}` }}>${fmt2(row.value)}</td>
                        <td style={{ padding:"6px 10px", color: row.value - netProfit > 0 ? UI.text.primary : UI.text.muted, borderBottom:`1px solid ${UI.line.hairline}`, fontWeight:600 }}>
                          {row.value - netProfit > 0 ? `+$${fmt2(row.value - netProfit)}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════
          CALCULATOR 2 — Dividend
      ══════════════════════════════════════════════ */}
      {calc === "dividend" && (
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

          {/* Inputs */}
          <div style={{ background:UI.surface.raised, borderRadius:T.radius.xl, boxShadow: UI.shadow.card, padding:"16px", border:`1px solid ${UI.line.hairline}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:6, fontWeight:800, fontSize:14, color:UI.text.primary, marginBottom:14 }}><Icon name="download" size={15} color={UI.text.primary}/> נתוני הדיבידנד</div>
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              <div style={ROW}>
                <div style={HALF}>
                  <SymbolAutocomplete label="סמל / שם מניה" value={divSymbol} onChange={setDivSymbol} placeholder="AAPL" inputStyle={INP} />
                </div>
                <div style={HALF}>
                  <label style={LBL}>כמות מניות</label>
                  <input style={INP} type="number" value={divShares} onChange={e => setDivShares(e.target.value)} placeholder="200"/>
                </div>
              </div>
              <div style={ROW}>
                <div style={HALF}>
                  <label style={LBL}>דיבידנד שנתי למניה ($)</label>
                  <input style={INP} type="number" step="0.01" value={annualDivPS} onChange={e => setAnnualDivPS(e.target.value)} placeholder="1.00"/>
                </div>
                <div style={HALF}>
                  <label style={LBL}>תשואת דיבידנד (%)</label>
                  <input style={INP} type="number" step="0.01" value={divYieldPct} onChange={e => setDivYieldPct(e.target.value)} placeholder="0.53"/>
                </div>
              </div>
              <div>
                <label style={LBL}>צמיחת דיבידנד שנתית צפויה: <strong>{divGrowthPct}%</strong></label>
                <input type="range" min="0" max="20" step="0.5" value={divGrowthPct} onChange={e => setDivGrowthPct(e.target.value)}
                  style={{ width:"100%", accentColor:UI.categorical[0], cursor:"pointer" }}/>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:UI.text.secondary, marginTop:2 }}>
                  <span>0%</span><span>10%</span><span>20%</span>
                </div>
              </div>
              <div>
                <label style={LBL}>שנות השקעה: <strong>{divYears} שנים</strong></label>
                <input type="range" min="1" max="20" step="1" value={divYears} onChange={e => setDivYears(+e.target.value)}
                  style={{ width:"100%", accentColor:UI.categorical[0], cursor:"pointer" }}/>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:UI.text.secondary, marginTop:2 }}>
                  <span>1</span><span>10</span><span>20</span>
                </div>
              </div>
              <div>
                <label style={LBL}>מדיניות מס</label>
                <div style={{ display:"flex", gap:8 }}>
                  {[{id:"IL",label:"ישראל · 25%"},{id:"US",label:"ארה\"ב · 15%"}].map(t => (
                    <button key={t.id} onClick={() => setDivTaxType(t.id)}
                      style={{ flex:1, background:divTaxType===t.id ? UI.state.surface : UI.surface.page, border:`1.5px solid ${divTaxType===t.id ? UI.state.surface : UI.line.hairline}`, borderRadius:6, padding:"10px 8px", fontSize:13, fontWeight:700, color:divTaxType===t.id ? UI.surface.page : UI.text.secondary, cursor:"pointer" }}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          {dShares > 0 && dDivPS > 0 && (
            <>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10 }}>
                <ResultCard label="דיבידנד שנתי" value={`$${fmt2(annualDivNow)}`} sub={divSymbol || "שנתי"} green={false}/>
                <ResultCard label="דיבידנד חודשי" value={`$${fmt2(monthlyDiv)}`}  sub="ממוצע"              green={false}/>
                <ResultCard label={`מס (${dTaxRate*100}%)`} value={`$${fmt2(taxOnDiv)}`} sub="שנתי"       green={false}/>
                <ResultCard label="נטו אחרי מס"   value={`$${fmt2(netAnnual)}`}   sub="שנתי"              green={true}/>
              </div>

              {/* 20-year projection chart */}
              <div style={{ background:UI.surface.raised, borderRadius:T.radius.xl, boxShadow: UI.shadow.card, padding:"16px", border:`1px solid ${UI.line.hairline}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, fontWeight:800, fontSize:14, color:UI.text.primary, marginBottom:2 }}>
                  <Icon name="trendUp" size={15} color={UI.text.primary}/> תחזית DRIP · {divYears} שנים עם השקעה מחדש
                </div>
                <div style={{ fontSize:11, color:UI.text.secondary, marginBottom:14 }}>
                  הדיבידנד הנקי מושקע מחדש לקניית מניות נוספות. צמיחה שנתית: {divGrowthPct}%
                </div>

                <div style={{ height:200, marginBottom:16 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={dripData.slice(0, divYears + 1)} margin={{ top:5, right:8, left:8, bottom:0 }}>
                      <defs>
                        <linearGradient id="divGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%"   stopColor={UI.categorical[2]} stopOpacity={0.06}/>
                          <stop offset="100%" stopColor={UI.categorical[2]} stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%"   stopColor={UI.categorical[0]} stopOpacity={0.05}/>
                          <stop offset="100%" stopColor={UI.categorical[0]} stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke={UI.chartGrid} vertical={false}/>
                      <XAxis dataKey="year" tick={{ fontSize:11, fill:UI.text.muted }} axisLine={false} tickLine={false} tickFormatter={v => `${v}y`}/>
                      <YAxis tick={{ fontSize:11, fill:UI.text.muted }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}K`} width={45}/>
                      <Tooltip formatter={(v, n) => [`$${fmt2(v)}`, n === "annualDiv" ? "דיבידנד שנתי" : "דיבידנד מצטבר"]} labelFormatter={l => `שנה ${l}`} contentStyle={{ fontSize:12, borderRadius:6, background:UI.surface.overlay, border:`1px solid ${UI.line.hairline}`, color:UI.text.primary }}/>
                      <Area type="monotone" dataKey="annualDiv" stroke={UI.categorical[2]} strokeWidth={1.5} fill="url(#divGrad)" name="annualDiv"/>
                      <Area type="monotone" dataKey="cumDiv"    stroke={UI.categorical[0]} strokeWidth={1.5} fill="url(#cumGrad)" name="cumDiv"/>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Summary */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
                  <div style={{ background:UI.fill.subtle, border:`1.5px solid `, borderRadius:6, padding:"12px 14px" }}>
                    <div style={{ fontSize:11, color:UI.text.secondary, fontWeight:700 }}>סך הכנסה מדיבידנד (נטו)</div>
                    <div style={{ fontSize:20, fontWeight:800, color:UI.text.primary, marginTop:4 }}>${fmt2(dripData[divYears].cumDiv)}</div>
                    <div style={{ fontSize:10, color:UI.text.muted, marginTop:2 }}>ב-{divYears} שנים</div>
                  </div>
                  <div style={{ background:UI.fill.subtle, border:`1.5px solid ${UI.line.strong}`, borderRadius:6, padding:"12px 14px" }}>
                    <div style={{ fontSize:11, color:UI.text.secondary, fontWeight:700 }}>מניות לאחר DRIP</div>
                    <div style={{ fontSize:20, fontWeight:800, color:UI.text.primary, marginTop:4 }}>{fmt2(dripData[divYears].shares)}</div>
                    <div style={{ fontSize:10, color:UI.text.muted, marginTop:2 }}>לעומת {fmt2(dShares)} כיום</div>
                  </div>
                </div>

                {/* Table */}
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                    <thead>
                      <tr style={{ background:UI.surface.page }}>
                        {["שנה","מניות","דיב׳ למניה","דיב׳ שנתי ברוטו","דיב׳ נטו","מצטבר"].map(h => (
                          <th key={h} style={{ padding:"8px 8px", textAlign:"right", fontWeight:700, color:UI.text.secondary, borderBottom:`2px solid ${UI.surface.sunken}`, whiteSpace:"nowrap", fontSize:11 }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {dripData.slice(1, divYears + 1).map(row => (
                        <tr key={row.year} style={{ background: row.year % 2 === 0 ? UI.surface.page : UI.surface.raised, fontWeight: row.year === divYears ? 800 : 400 }}>
                          <td style={{ padding:"6px 8px", color:UI.text.secondary, borderBottom:`1px solid ${UI.line.hairline}` }}>שנה {row.year}</td>
                          <td style={{ padding:"6px 8px", borderBottom:`1px solid ${UI.line.hairline}`, color:UI.text.primary }}>{fmt2(row.shares)}</td>
                          <td style={{ padding:"6px 8px", borderBottom:`1px solid ${UI.line.hairline}`, color:UI.text.primary }}>${row.dps}</td>
                          <td style={{ padding:"6px 8px", borderBottom:`1px solid ${UI.line.hairline}`, color:UI.text.primary }}>${fmt2(row.annualDiv)}</td>
                          <td style={{ padding:"6px 8px", borderBottom:`1px solid ${UI.line.hairline}`, color:UI.text.primary, fontWeight:600 }}>${fmt2(row.netDiv)}</td>
                          <td style={{ padding:"6px 8px", borderBottom:`1px solid ${UI.line.hairline}`, color:UI.text.primary, fontWeight:600 }}>${fmt2(row.cumDiv)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CalculatorsTab;
